import { uniq } from 'es-toolkit';
import React from 'react';
import { Checkbox, Divider, FormControlLabel, Stack } from '@mui/material';

import { INDETERMINATE, isIndeterminate } from '@paragon/client-tools';
import { BearingEnvelope } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { DistanceField, NumericField } from '@paragon/material';

import { TransformationButtons } from '../../../../library/transformations/controls/TransformationButtons';
import { editBearingEnvelopes } from '../../../../store/actions/layout/bearingEnvelopeActions';
import {
  bearingEnvelopeElevationsMatch,
  getActiveLayoutLevelGuidOrNull,
  getBearingEnvelopeElevationsFromActiveLevel,
} from '../../../../store/selectors/layoutDataSelectors';
import { isLayoutModeViewOnly } from '../../../../store/selectors/layoutModeSelectors';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { ProjectHierarchyContextSelects } from './ProjectHierarchyContextSelects';
import { PropertiesPanelMenuButton } from './PropertiesPanelMenuButton';
import { getThicknessAndStructuralToggleRowSx } from './roof-planes/bearing-envelopes/BearingEnvelopeCommonProperties';
import { SyncElevationButton } from './SyncElevationButton';

/*
  Note that we strongly desire to keep this panel as visually similar to the singular `BearingEnvelopeProperties` panel
    as possible. Please consider updating both panels when making any changes.
 */
export function BulkBearingEnvelopeProperties(props: { type: 'beam' | 'wall'; envelopes: BearingEnvelope[] }) {
  const { type, envelopes } = props;

  const dispatch = useAppDispatch();
  const viewOnlyProperties = useAppSelector(propertiesShouldBeViewOnly);
  const viewOnlyLayout = useAppSelector(isLayoutModeViewOnly);
  const viewOnly = viewOnlyProperties || viewOnlyLayout;

  const activeLevelGuid = useAppSelector(getActiveLayoutLevelGuidOrNull);
  const elevationsFromLevel = useAppSelector(getBearingEnvelopeElevationsFromActiveLevel);

  const levelGuids = uniq(envelopes.map((envelope) => envelope.levelGuid));
  const commonLevelGuid = levelGuids.length === 1 ? levelGuids[0] : INDETERMINATE;

  const levelIsSuitableForSync =
    type === 'wall' &&
    activeLevelGuid != null &&
    commonLevelGuid === activeLevelGuid &&
    elevationsFromLevel.top != null &&
    elevationsFromLevel.bottom != null;

  const thicknesses = uniq(envelopes.map((envelope) => envelope.thickness));
  const commonThickness = thicknesses.length === 1 ? thicknesses[0] : INDETERMINATE;

  const plyCounts = uniq(envelopes.map((envelope) => envelope.numberOfPlies));
  const commonPlyCount = plyCounts.length === 1 ? plyCounts[0] : INDETERMINATE;

  const topElevations = uniq(envelopes.map((envelope) => envelope.top));
  const commonTopElevation = topElevations.length === 1 ? topElevations[0] : INDETERMINATE;

  const bottomElevations = uniq(envelopes.map((envelope) => envelope.bottom));
  const commonBottomElevation = bottomElevations.length === 1 ? bottomElevations[0] : INDETERMINATE;

  const someStructural = envelopes.some((envelope) => !envelope.nonStructural);
  const allStructural = envelopes.every((envelope) => !envelope.nonStructural);

  function editAllEnvelopes(editedProperties: Partial<Omit<BearingEnvelope, 'guid'>>) {
    return dispatch(
      editBearingEnvelopes(
        envelopes.map((envelope) => ({
          guid: envelope.guid,
          editedProperties,
        })),
      ),
    );
  }

  return (
    <PropertiesPanel
      title={type === 'beam' ? 'Multiple Beams' : 'Multiple Walls'}
      titleButton={<PropertiesPanelMenuButton type={type} guid={envelopes[0].guid} />}
    >
      <Stack direction="column" spacing={2}>
        <TransformationButtons disabled={viewOnly} />

        <Divider />

        {!isIndeterminate(commonLevelGuid) && (
          <>
            <ProjectHierarchyContextSelects
              levelGuid={commonLevelGuid}
              onChange={(levelGuid) => editAllEnvelopes({ levelGuid })}
              disabled={viewOnly}
            />

            <Divider />
          </>
        )}

        {levelIsSuitableForSync && (
          <SyncElevationButton
            plural // because bearing envelopes have a top and bottom elevation
            disabled={viewOnly}
            synchronized={
              typeof commonTopElevation === 'number' &&
              typeof commonBottomElevation === 'number' &&
              bearingEnvelopeElevationsMatch(elevationsFromLevel, {
                top: commonTopElevation,
                bottom: commonBottomElevation,
              })
            }
            onSynchronize={async () => {
              await editAllEnvelopes(elevationsFromLevel);
            }}
          />
        )}

        <Stack direction="row" spacing={1} sx={{ marginBottom: 1 }}>
          <DistanceField
            label="Top Elevation"
            canBeIndeterminate
            value={commonTopElevation}
            onSubmit={async (top) => {
              await editAllEnvelopes({ top });
            }}
            disabled={viewOnly}
          />

          <DistanceField
            label="Bottom Elevation"
            canBeIndeterminate
            value={commonBottomElevation}
            onSubmit={async (bottom) => {
              await editAllEnvelopes({ bottom });
            }}
            disabled={viewOnly}
          />
        </Stack>

        <Divider />

        <Stack direction="row" spacing={1} sx={getThicknessAndStructuralToggleRowSx(2)}>
          <DistanceField
            label="Thickness"
            canBeIndeterminate
            disabled={viewOnly}
            min={1 / 16}
            value={commonThickness}
            onSubmit={async (thickness) => {
              await editAllEnvelopes({ thickness });
            }}
          />

          {type === 'beam' && (
            <NumericField
              label="Number of Plies"
              canBeIndeterminate
              disabled={viewOnly}
              min={1}
              integers
              value={commonPlyCount}
              onSubmit={async (numberOfPlies) => {
                await editAllEnvelopes({ numberOfPlies });
              }}
            />
          )}

          {type === 'wall' && (
            <FormControlLabel
              control={
                <Checkbox
                  disabled={viewOnly}
                  checked={allStructural}
                  indeterminate={someStructural && !allStructural}
                  onChange={async (_, checked) => {
                    await editAllEnvelopes({ nonStructural: !checked });
                  }}
                />
              }
              label="Structural"
            />
          )}
        </Stack>
      </Stack>
    </PropertiesPanel>
  );
}
